import React, { useEffect, useRef } from "react";
import { drawSwitchSwapAnim, drawSwitchScene, SWITCH_ANIMS, HIDE_HEADS, type SwapEnd } from "../../game-canvas/switch-anim";

// Multi-worm scenarios: exactly the in-game #31 (drawSwitchScene), for the player-level
// ownership combinations. Each has worms {x,y,playerId} + an ownerMap (oldPlayer -> newPlayer).
const P_COLORS: Record<string, string> = { A: "#4aa3ff", B: "#ff5c7a", C: "#7CFC66" };
const SCENARIOS = [
  {
    label: "1 vs 1 (baseline)",
    worms: [{ x: 90, y: 60, playerId: "A" }, { x: 210, y: 95, playerId: "B" }],
    map: { A: "B", B: "A" },
  },
  {
    label: "2 worms (A) <-> 1 worm (B)",
    worms: [{ x: 70, y: 45, playerId: "A" }, { x: 95, y: 110, playerId: "A" }, { x: 220, y: 75, playerId: "B" }],
    map: { A: "B", B: "A" }, // A's two worms become B's; B's one worm becomes A's
  },
  {
    label: "1 worm (A) <-> 2 worms (B)",
    worms: [{ x: 80, y: 75, playerId: "A" }, { x: 205, y: 40, playerId: "B" }, { x: 230, y: 110, playerId: "B" }],
    map: { A: "B", B: "A" },
  },
  {
    label: "2 vs 2",
    worms: [{ x: 60, y: 50, playerId: "A" }, { x: 90, y: 110, playerId: "A" }, { x: 210, y: 45, playerId: "B" }, { x: 235, y: 105, playerId: "B" }],
    map: { A: "B", B: "A" },
  },
  {
    label: "3-player cycle (A->B->C->A)",
    worms: [{ x: 60, y: 55, playerId: "A" }, { x: 150, y: 110, playerId: "B" }, { x: 240, y: 55, playerId: "C" }],
    map: { A: "B", B: "C", C: "A" },
  },
  {
    label: "3-player, mixed counts",
    worms: [{ x: 55, y: 45, playerId: "A" }, { x: 80, y: 110, playerId: "A" }, { x: 155, y: 65, playerId: "B" }, { x: 240, y: 100, playerId: "C" }],
    map: { A: "B", B: "C", C: "A" },
  },
];

// Dev-only gallery of SWITCHAROONIE swap animations (route: /sw). Each tile loops:
// idle (frozen heads) -> swap effect -> heads wear their NEW owner's colour -> idle ->
// swap back. Worms never move at a real switch - ownership (head colour from that
// point) is what swaps - so every design is "something travels between the worms".
const TILE_W = 300;
const TILE_H = 150;
const HEAD_R = 9;
const BLUE = "#4aa3ff";
const RED = "#ff5c7a";

// lifecycle timing (seconds): matches the in-game freeze HOLD (~1.2s) for the swap.
const T_IDLE = 0.7, T_SWAP = 1.2;
const TOTAL = (T_IDLE + T_SWAP) * 2; // there and back again

const A = { x: 70, y: 95 };
const B = { x: 230, y: 55 };

// A little frozen "trail" behind each head so the tile reads as two worms, not two dots.
function drawWormStub(ctx: CanvasRenderingContext2D, x: number, y: number, dirX: number, color: string) {
  ctx.strokeStyle = color;
  ctx.lineWidth = HEAD_R * 1.6;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(x - dirX * 8, y + 4);
  ctx.quadraticCurveTo(x - dirX * 30, y + 14, x - dirX * 52, y + 6);
  ctx.stroke();
}

export default function SwitchAnimDemo() {
  const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);
  const scenRefs = useRef<(HTMLCanvasElement | null)[]>([]);
  const rafRef = useRef<number>(0);
  const startRef = useRef<number>(0);

  useEffect(() => {
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    canvasRefs.current.forEach((c) => { if (c) { c.width = TILE_W * dpr; c.height = TILE_H * dpr; } });
    scenRefs.current.forEach((c) => { if (c) { c.width = TILE_W * dpr; c.height = TILE_H * dpr; } });

    function loop(now: number) {
      if (!startRef.current) startRef.current = now;
      const t = (now - startRef.current) / 1000;
      // Multi-worm scenarios: the real in-game #31 (drawSwitchScene), frozen worms + swap FX.
      SCENARIOS.forEach((sc, idx) => {
        const c = scenRefs.current[idx];
        const ctx = c && c.getContext("2d");
        if (!ctx) return;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, TILE_W, TILE_H);
        const lt = (t + idx * 0.2) % TOTAL;
        const p = lt < T_IDLE ? 0 : Math.min(1, (lt - T_IDLE) / T_SWAP);
        // Draw each worm stub + head; colour flips to the NEW owner at go (p >= 1). During the
        // swap the black portal discs cover the head, so we keep the old colour until p ~ 0.85.
        for (const w of sc.worms) {
          const newOwner = (sc.map as Record<string, string>)[w.playerId];
          const col = p >= 0.85 ? P_COLORS[newOwner] : P_COLORS[w.playerId];
          drawWormStub(ctx, w.x, w.y, w.x < TILE_W / 2 ? 1 : -1, col);
          ctx.fillStyle = "#FFB74D";
          ctx.beginPath(); ctx.arc(w.x, w.y, HEAD_R * 0.7, 0, Math.PI * 2); ctx.fill();
        }
        if (p > 0 && p < 1) {
          const worms = sc.worms.map((w) => ({ x: w.x, y: w.y, headR: HEAD_R, playerId: w.playerId }));
          drawSwitchScene(ctx, worms, sc.map, (pid: any) => P_COLORS[pid] || "#fff", p, t);
        }
      });
      SWITCH_ANIMS.forEach((v, idx) => {
        const c = canvasRefs.current[idx];
        const ctx = c && c.getContext("2d");
        if (!ctx) return;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, TILE_W, TILE_H);
        const lt = (t + idx * 0.21) % TOTAL;
        // First half: A blue / B red, swapping to the opposite. Second half: swap back.
        const secondHalf = lt >= TOTAL / 2;
        const ht = secondHalf ? lt - TOTAL / 2 : lt;
        const aFrom = secondHalf ? RED : BLUE, bFrom = secondHalf ? BLUE : RED;
        const p = ht < T_IDLE ? 0 : Math.min(1, (ht - T_IDLE) / T_SWAP);
        const a: SwapEnd = { ...A, colorFrom: aFrom, colorTo: bFrom };
        const b: SwapEnd = { ...B, colorFrom: bFrom, colorTo: aFrom };
        // Heads: colour flips halfway through the swap; some designs hide them mid-swap.
        const hide = HIDE_HEADS[v.n];
        const hidden = hide && p > hide[0] && p < hide[1];
        const aCol = p > 0.5 ? a.colorTo : a.colorFrom;
        const bCol = p > 0.5 ? b.colorTo : b.colorFrom;
        drawWormStub(ctx, A.x, A.y, 1, aCol);
        drawWormStub(ctx, B.x, B.y, -1, bCol);
        if (!hidden) {
          ctx.fillStyle = "#FFB74D"; // the shared head colour, like in game
          ctx.beginPath(); ctx.arc(A.x, A.y, HEAD_R * 0.8, 0, Math.PI * 2); ctx.fill();
          ctx.beginPath(); ctx.arc(B.x, B.y, HEAD_R * 0.8, 0, Math.PI * 2); ctx.fill();
        }
        if (p > 0 && p < 1) drawSwitchSwapAnim(ctx, a, b, HEAD_R, p, t, v.n);
      });
      rafRef.current = window.requestAnimationFrame(loop);
    }
    rafRef.current = window.requestAnimationFrame(loop);
    return () => window.cancelAnimationFrame(rafRef.current);
  }, []);

  return (
    <div style={{ background: "#05070d", minHeight: "100vh", padding: 24, color: "#eaf0ff", fontFamily: "'Rajdhani',sans-serif" }}>
      <h1 style={{ fontFamily: "'Orbitron',sans-serif", fontSize: 20, letterSpacing: 2 }}>SWITCHAROONIE SWAP ANIMATIONS</h1>
      <p style={{ color: "#8995b3", maxWidth: 720 }}>
        Each tile loops: frozen worms, the swap effect carries the colours across, worms resume
        under their new owners. Worms never move at a real switch, so every design is about what
        travels BETWEEN them.
      </p>

      <h2 style={{ fontSize: 15, color: "#7CFC66", letterSpacing: 1, marginTop: 8 }}>CHOSEN: #31 in-game, worm-count combinations</h2>
      <p style={{ color: "#8995b3", maxWidth: 720, marginTop: 0 }}>
        The real switch is player-level, so counts vary. This is the exact in-game renderer
        (drawSwitchScene): a portal over every worm, and each worm's NEW colour flies in from the
        centroid of that owner's OLD cluster. Handles 1&lt;-&gt;many, many&lt;-&gt;1, many&lt;-&gt;many, N-player cycles.
      </p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 18, marginBottom: 20 }}>
        {SCENARIOS.map((sc, idx) => (
          <div key={sc.label} style={{ width: TILE_W }}>
            <canvas
              ref={(el) => { scenRefs.current[idx] = el; }}
              style={{ width: TILE_W, height: TILE_H, background: "#0b0f1a", border: "1px solid #2b3a2b", borderRadius: 8 }}
            />
            <div style={{ fontSize: 13, color: "#cbd3e6", marginTop: 4 }}>{sc.label}</div>
          </div>
        ))}
      </div>

      <h2 style={{ fontSize: 15, color: "#cbd3e6", letterSpacing: 1 }}>All swap-design candidates</h2>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 18 }}>
        {SWITCH_ANIMS.map((v, idx) => (
          <div key={v.n} style={{ width: TILE_W }}>
            <canvas
              ref={(el) => { canvasRefs.current[idx] = el; }}
              style={{ width: TILE_W, height: TILE_H, background: "#0b0f1a", border: "1px solid #1e2636", borderRadius: 8 }}
            />
            <div style={{ fontSize: 13, color: "#cbd3e6", marginTop: 4 }}>#{v.n} {v.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
